import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { SEO } from '../components/shared/SEO';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { PharmacistChat } from '../components/services/PharmacistChat';
import { AnnouncementBar } from '../components/layout/AnnouncementBar';
import { MobileDeliverToPill } from '../components/layout/MobileDeliverToPill';
import { PageHeroBreadcrumb } from '../components/layout/PageHeroBreadcrumb';
import { MobileHomeHeader } from '../components/MobileHomeHeader';
import { LocationModal } from '../components/modals/LocationModal';
import { AlertCircleIcon, PackageIcon, CreditCardIcon, TruckIcon, ShoppingBagIcon, UserIcon, UploadIcon, CheckCircleIcon, ChevronRightIcon, HomeIcon } from 'lucide-react';
export function ReportIssuePage() {
  const navigate = useNavigate();
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);
  const [formData, setFormData] = useState({
    issueType: '',
    orderNumber: '',
    name: '',
    email: '',
    phone: '',
    subject: '',
    description: '',
    attachments: [] as File[]
  });
  const [submitted, setSubmitted] = useState(false);
  const issueTypes = [{
    value: 'order',
    label: 'Order Issue',
    icon: PackageIcon
  }, {
    value: 'payment',
    label: 'Payment Issue',
    icon: CreditCardIcon
  }, {
    value: 'delivery',
    label: 'Delivery Issue',
    icon: TruckIcon
  }, {
    value: 'product',
    label: 'Product Quality',
    icon: ShoppingBagIcon
  }, {
    value: 'account',
    label: 'Account Issue',
    icon: UserIcon
  }, {
    value: 'other',
    label: 'Other',
    icon: AlertCircleIcon
  }];
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFormData({
        ...formData,
        attachments: Array.from(e.target.files)
      });
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Issue reported:', formData);
    setSubmitted(true);
  };
  if (submitted) {
    return <HelmetProvider data-id="element-7935" data-mp="2nymltzx6ah3s">
        <SEO title="Issue Reported Successfully" description="Your issue has been submitted successfully. Our team will contact you soon." canonical="/report-issue" data-id="element-7936" data-mp="2ny4kwpzkfepj" />
        <div className="flex flex-col min-h-screen bg-gray-50" data-id="element-7937" data-mp="3gicupee73igk">
          {/* Desktop Header */}
          <div className="hidden md:block" data-id="element-7938" data-mp="3bnytuwgrkj4i">
            <AnnouncementBar data-id="element-7939" data-mp="3jz7gka837t80" />
            <MobileDeliverToPill selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} data-id="element-7940" data-mp="3k08vips84zv0" />
            <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} data-id="element-7941" data-mp="3syujx1kdn93p" />
          </div>
          <main className="flex-grow pt-[var(--header-height-mobile)] md:pt-[var(--header-height-desktop)]" data-id="element-7942" data-mp="athkxi68w5sb">
            {/* Mobile Header Overlay */}
            <div className="md:hidden fixed top-0 left-0 right-0 z-50" data-id="element-7943" data-mp="flitat0alcd2">
              <MobileHomeHeader selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} alwaysWhiteBackground={true} data-id="element-7944" data-mp="kjkd5g5jlfpl" />
            </div>
            <PageHeroBreadcrumb breadcrumbs={[{
            label: 'Home',
            href: '/'
          }, {
            label: 'Report an Issue'
          }]} title="Issue Reported Successfully" description="Your issue has been submitted. Our team will contact you soon." data-id="element-7945" data-mp="ui033fsu2ixi" />
            <div className="container mx-auto px-4 py-12" data-id="element-7946" data-mp="1w0dhglsnjtxu">
              <div className="max-w-2xl mx-auto text-center" data-id="element-7947" data-mp="18b2fx23h9vin">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-7948" data-mp="13awl5d1wvswc">
                  <CheckCircleIcon className="w-12 h-12 text-green-600" data-id="element-7949" data-mp="1hb7ceh74v9i1" />
                </div>
                <h1 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-7950" data-mp="1d9ceznsbpg9j">
                  Issue Reported Successfully
                </h1>
                <p className="text-lg text-gray-600 mb-8" data-id="element-7951" data-mp="2sojhswijk06b">
                  Thank you for reporting this issue. We have received your
                  report and our team will review it shortly. You will receive
                  an email confirmation at <strong data-id="element-7952" data-mp="2wa4sdbm8hly7">{formData.email}</strong>{' '}
                  with a reference number.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8" data-id="element-7953" data-mp="2m9uh3wjreolo">
                  <h3 className="font-semibold text-gray-900 mb-2" data-id="element-7954" data-mp="2r8pblvr3a1lm">
                    What happens next?
                  </h3>
                  <ul className="text-left text-gray-700 space-y-2" data-id="element-7955" data-mp="3ey05cta0uvq9">
                    <li className="flex items-start" data-id="element-7956" data-mp="3jxcwvrbu8qz6">
                      <span className="text-blue-600 mr-2" data-id="element-7957" data-mp="3jwtigkleckpw">•</span>
                      Our support team will review your issue within 24 hours
                    </li>
                    <li className="flex items-start" data-id="element-7958" data-mp="3a6rr9jxqym6r">
                      <span className="text-blue-600 mr-2" data-id="element-7959" data-mp="3a56xvxn659ah">•</span>
                      You will receive updates via email and SMS
                    </li>
                    <li className="flex items-start" data-id="element-7960" data-mp="6jhy2trwnk7p">
                      <span className="text-blue-600 mr-2" data-id="element-7961" data-mp="gegi6b8d4jft">•</span>
                      We may contact you for additional information if needed
                    </li>
                  </ul>
                </div>
                <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-7962" data-mp="7dieju1u6dxr">
                  <a href="/" className="inline-block bg-[#1E2B55] text-white font-bold px-8 py-3 rounded-lg hover:bg-[#162041] transition-colors" data-id="element-7963" data-mp="2enwwvfluvf8">
                    Back to Home
                  </a>
                  <a href="/account/orders" className="inline-block border-2 border-[#1E2B55] text-[#1E2B55] font-bold px-8 py-3 rounded-lg hover:bg-[#F1F2F6] transition-colors" data-id="element-7964" data-mp="3osp6v87rkq2p">
                    View My Orders
                  </a>
                </div>
              </div>
            </div>
          </main>
          <Footer data-id="element-7965" data-mp="35v4evs9pdbdv" />
          <MobileFooterNav data-id="element-7966" data-mp="35wpazmbo12du" />
          <PharmacistChat data-id="element-7967" data-mp="35xh6yy96d24n" />
          <div className="h-16 md:hidden" data-id="element-7968" data-mp="1ldlt9g1d7t53"></div>
          {/* Location Modal */}
          <LocationModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelectLocation={location => {
          setSelectedLocation(location);
          setLocationModalOpen(false);
        }} data-id="element-7969" data-mp="1lfpu1p3ywc15" />
        </div>
      </HelmetProvider>;
  }
  return <HelmetProvider data-id="element-7970" data-mp="12ndsh4l51es2">
      <SEO title="Report an Issue" description="Having a problem with your order or service? Report an issue and our support team will help you resolve it quickly." canonical="/report-issue" data-id="element-7971" data-mp="12o5fhr0o646q" />
      <div className="flex flex-col min-h-screen bg-gray-50" data-id="element-7972" data-mp="1bm92ysv7tb16">
        {/* Desktop Header */}
        <div className="hidden md:block" data-id="element-7973" data-mp="1gfjza02ei9eu">
          <AnnouncementBar data-id="element-7974" data-mp="jwmchk6naq4p" />
          <MobileDeliverToPill selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} data-id="element-7975" data-mp="jvbc7uces0ic" />
          <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} data-id="element-7976" data-mp="3dg1hayr4ghfy" />
        </div>
        <main className="flex-grow pt-[var(--header-height-mobile)] md:pt-[var(--header-height-desktop)]" data-id="element-7977" data-mp="3nez0rn9oxks3">
          {/* Mobile Header Overlay */}
          <div className="md:hidden fixed top-0 left-0 right-0 z-50" data-id="element-7978" data-mp="9htv28puv23">
            <MobileHomeHeader selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} alwaysWhiteBackground={true} data-id="element-7979" data-mp="7op5shnykwq" />
          </div>
          <PageHeroBreadcrumb breadcrumbs={[{
          label: 'Home',
          href: '/'
        }, {
          label: 'Report an Issue'
        }]} title="Report an Issue" description="We're here to help resolve any problems you're experiencing" data-id="element-7980" data-mp="a15jqypmk41g" />
          <div className="container mx-auto px-4 py-8" data-id="element-7981" data-mp="xnhgrb9hj2vm">
            <div className="max-w-4xl mx-auto" data-id="element-7982" data-mp="12masoz94ilto">
              {/* Issue Type Selection */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6" data-id="element-7983" data-mp="omja0l7nwh9c">
                <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-7984" data-mp="tkunr712vbwu">
                  What type of issue are you experiencing?
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-7985" data-mp="eun972e6yd1f">
                  {issueTypes.map(type => {
                  const Icon = type.icon;
                  return <button key={type.value} onClick={() => setFormData({
                    ...formData,
                    issueType: type.value
                  })} className={`flex flex-col items-center p-4 rounded-lg border-2 transition-all ${formData.issueType === type.value ? 'border-[#1E2B55] bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-7986" data-mp="5x3b22bmkwv1">
                        <Icon className={`w-8 h-8 mb-2 ${formData.issueType === type.value ? 'text-[#1E2B55]' : 'text-gray-400'}`} data-id="element-7987" data-mp="42vn2ak0mjbt" />
                        <span className="text-sm font-medium text-center" data-id="element-7988" data-mp="942w0cxw9ok2">
                          {type.label}
                        </span>
                      </button>;
                })}
                </div>
              </div>
              {/* Issue Form */}
              <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-7989" data-mp="n1hq3uz3ekid">
                <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-7990" data-mp="1fq5h876iw43u">
                  Tell us more about the issue
                </h2>
                <div className="space-y-6" data-id="element-7991" data-mp="1kigh5a19jpwe">
                  {/* Order Number (Optional) */}
                  <div data-id="element-7992" data-mp="355pwfy9wx42c">
                    <label htmlFor="orderNumber" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7993" data-mp="308harnpk2hwc">
                      Order Number (Optional)
                    </label>
                    <input type="text" id="orderNumber" name="orderNumber" value={formData.orderNumber} onChange={handleInputChange} placeholder="e.g., ORD-12345" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-7994" data-mp="39zbyyxmd3rkf" />
                  </div>
                  {/* Name */}
                  <div data-id="element-7995" data-mp="al8ksjngazfx">
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-7996" data-mp="5k2mm6b0ujz0">
                      Full Name <span className="text-red-500" data-id="element-7997" data-mp="2ze50n46g2lj7">*</span>
                    </label>
                    <input type="text" id="name" name="name" value={formData.name} onChange={handleInputChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-7998" data-mp="349anc57o848c" />
                  </div>
                  {/* Email & Phone */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-7999" data-mp="2pkwmfzttepuf">
                    <div data-id="element-8000" data-mp="2udpr6npa9y6i">
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-8001" data-mp="2uf0tsk6zszw1">
                        Email Address <span className="text-red-500" data-id="element-8002" data-mp="21utwbp9gntyg">*</span>
                      </label>
                      <input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-8003" data-mp="1uclys2psols6" />
                    </div>
                    <div data-id="element-8004" data-mp="16mi4wn2es0ed">
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-8005" data-mp="16m8gh3x0i1as">
                        Phone Number <span className="text-red-500" data-id="element-8006" data-mp="wqyegg6m5tx0">*</span>
                      </label>
                      <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleInputChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-8007" data-mp="smgy6qzgpyqz" />
                    </div>
                  </div>
                  {/* Subject */}
                  <div data-id="element-8008" data-mp="242xow4325o12">
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-8009" data-mp="242fvdf7s5nwu">
                      Subject <span className="text-red-500" data-id="element-8010" data-mp="1v29bnblhnrp8">*</span>
                    </label>
                    <input type="text" id="subject" name="subject" value={formData.subject} onChange={handleInputChange} required placeholder="Brief description of the issue" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-8011" data-mp="1v4m0m8pnz2bs" />
                  </div>
                  {/* Description */}
                  <div data-id="element-8012" data-mp="h7svuyghvv6c">
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-8013" data-mp="h82mmo9d66d3">
                      Detailed Description{' '}
                      <span className="text-red-500" data-id="element-8014" data-mp="m5ktm93tmbig">*</span>
                    </label>
                    <textarea id="description" name="description" value={formData.description} onChange={handleInputChange} required rows={6} placeholder="Please provide as much detail as possible about the issue you're experiencing..." className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent resize-none" data-id="element-8015" data-mp="1053pa2fg3udv" />
                  </div>
                  {/* File Upload */}
                  <div data-id="element-8016" data-mp="1arm300uvxcsd">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-8017" data-mp="1auhy7hfclnd4">
                      Attachments (Optional)
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#1E2B55] transition-colors" data-id="element-8018" data-mp="1js226y4lhxrf">
                      <UploadIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" data-id="element-8019" data-mp="1oq3n7oll075j" />
                      <p className="text-sm text-gray-600 mb-2" data-id="element-8020" data-mp="1tjoktfj9iz47">
                        Upload images or documents to help us understand the
                        issue
                      </p>
                      <input type="file" id="attachments" name="attachments" onChange={handleFileChange} multiple accept="image/*,.pdf,.doc,.docx" className="hidden" data-id="element-8021" data-mp="23ixbnzitzxmc" />
                      <label htmlFor="attachments" className="inline-block bg-gray-100 text-gray-700 px-4 py-2 rounded-lg cursor-pointer hover:bg-gray-200 transition-colors" data-id="element-8022" data-mp="3iz8picp10gog">
                        Choose Files
                      </label>
                      {formData.attachments.length > 0 && <p className="text-sm text-gray-600 mt-2" data-id="element-8023" data-mp="5tidvvkfs9zl">
                          {formData.attachments.length} file(s) selected
                        </p>}
                    </div>
                  </div>
                  {/* Submit Button */}
                  <button type="submit" disabled={!formData.issueType} className="w-full bg-[#1E2B55] text-white font-bold py-4 rounded-lg hover:bg-[#162041] transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed" data-id="element-8024" data-mp="ybw4ba3g0ton">
                    Submit Issue Report
                  </button>
                </div>
              </form>
              {/* Help Info */}
              <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6" data-id="element-8025" data-mp="3t11duayukjtd">
                <h3 className="font-semibold text-gray-900 mb-2" data-id="element-8026" data-mp="klhnpkth3hij">
                  Need immediate assistance?
                </h3>
                <p className="text-gray-700 mb-4" data-id="element-8027" data-mp="pjsypyvi7y2c">
                  For urgent issues, you can contact our support team directly:
                </p>
                <div className="flex flex-col sm:flex-row gap-4" data-id="element-8028" data-mp="gke72w3lphg9">
                  <a href="tel:+966920000000" className="inline-block bg-white text-[#1E2B55] font-medium px-6 py-2 rounded-lg border-2 border-[#1E2B55] hover:bg-[#F1F2F6] transition-colors text-center" data-id="element-8029" data-mp="gil0thwwgz8s">
                    Call: +966 920 000 000
                  </a>
                  <a href="/contact-us" className="inline-block bg-white text-[#1E2B55] font-medium px-6 py-2 rounded-lg border-2 border-[#1E2B55] hover:bg-[#F1F2F6] transition-colors text-center" data-id="element-8030" data-mp="2os1acjlyozz1">
                    Live Chat Support
                  </a>
                </div>
              </div>
            </div>
          </div>
        </main>
        <Footer data-id="element-8031" data-mp="2541zz9vb7uu5" />
        <MobileFooterNav data-id="element-8032" data-mp="254k04hl3et7c" />
        <PharmacistChat data-id="element-8033" data-mp="255cz2wdt8eex" />
        <div className="h-16 md:hidden" data-id="element-8034" data-mp="251yxhqcz098j"></div>
        {/* Location Modal */}
        <LocationModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelectLocation={location => {
        setSelectedLocation(location);
        setLocationModalOpen(false);
      }} data-id="element-8035" data-mp="2jtpwlxsjq5bj" />
      </div>
    </HelmetProvider>;
}